import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import TrainingTaskModal from "../../pages/Training/TrainingTaskModal";
import Pagination from "../../components/Pagination";
import { useParams, useNavigate, useLocation } from "react-router-dom";

function TrainingPanel() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();

  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;
  const location = useLocation();
  const initialOpenKey = location.state?.openModuleKey || null;

  const [trainings, setTrainings] = useState([]);
  const [tab, setTab] = useState("assigned");

  const [showModal, setShowModal] = useState(false);
  const [activeModule, setActiveModule] = useState("");
  const [activeTitle, setActiveTitle] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedScore, setSelectedScore] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const getPaginatedData = (data, currentPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const fetchTrainings = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/trainings/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const processed = res.data.map((item) => ({
        ...item,
        status: item.tp_status || "not-started",
        score: item.tp_score ?? null,
        startDate: item.tp_start_date
          ? new Date(item.tp_start_date).toLocaleDateString("en-CA")
          : "",
        finishDate: item.tp_finish_date
          ? new Date(item.tp_finish_date).toLocaleDateString("en-CA")
          : "",
        deadline: item.tm_deadline
          ? new Date(item.tm_deadline).toLocaleDateString("en-CA")
          : "",
      }));
      setTrainings(processed);
      setCurrentPage(1);
    } catch (err) {
      console.error("Failed to load trainings", err);
    }
  }, [BASE_URL, userId]);

  useEffect(() => {
    fetchTrainings();
  }, [fetchTrainings]);

  useEffect(() => {
    if (!initialOpenKey || trainings.length === 0) return;
    const t = trainings.find((x) => x.tm_key === initialOpenKey);
    if (t) {
      handleOpenQuiz(t.tm_key, t.tm_title, t.status, t.score);
      // clear the state so it doesn’t re-open if the user comes back
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [initialOpenKey, trainings, navigate, location.pathname]);

  const formatStatusIcon = (status, score) => {
    if (status === "complete" && score >= 50)
      return <span className="text-success">✔️</span>;
    if (status === "complete" && score < 50)
      return <span className="text-danger">🔁</span>;
    if (status === "in-progress")
      return <span className="text-warning">◐</span>;
    return <span className="text-danger">●</span>;
  };

  const handleOpenQuiz = (moduleKey, title, status, score) => {
    setActiveModule(moduleKey);
    setActiveTitle(title);
    setSelectedStatus(status);
    setSelectedScore(score);
    setShowModal(true);
  };

  const handleReturn = () => {
    if (isAdmin && paramId) {
      navigate(`/userhome/${paramId}`);
    } else {
      navigate("/userhome");
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Pg. 5 - Training Panel
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleReturn}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-4 text-start" />
          <div className="col-4 text-center">
            <h3 className="mb-0">Pg. 5 - Training Panel</h3>
          </div>
          <div className="col-4 text-end">
            <div>New‑Way Trucking Ltd</div>
            <div>7725 48 St. SE, Calgary</div>
            <div>Alberta, Canada T2C 2V3</div>
          </div>
        </div>

        <div className="d-flex justify-content-end mb-3">
          <button
            className={`btn btn-outline-primary me-2 ${
              tab === "assigned" ? "active" : ""
            }`}
            onClick={() => setTab("assigned")}
          >
            Assigned
          </button>
          <button
            className={`btn btn-outline-secondary ${
              tab === "review" ? "active" : ""
            }`}
            onClick={() => setTab("review")}
          >
            Review
          </button>
        </div>

        {tab === "assigned" && (
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Status</th>
                  <th>Module Name</th>
                  <th>Score</th>
                  {/* <th>Deadline</th> */}
                  <th>Start Date</th>
                  <th>Finish Date</th>
                </tr>
              </thead>
              <tbody>
                {getPaginatedData(trainings, currentPage).map((t) => (
                  <tr key={t.tm_id}>
                    <td>{formatStatusIcon(t.status, t.score)}</td>
                    <td>
                      <button
                        className="btn btn-link"
                        onClick={() =>
                          handleOpenQuiz(
                            t.tm_key,
                            t.tm_title,
                            t.status,
                            t.score
                          )
                        }
                      >
                        {t.tm_title}
                      </button>
                    </td>
                    <td> {t.score || "-"}</td>
                    {/* <td>{t.deadline || "-"}</td> */}
                    <td>{t.startDate || "-"}</td>
                    <td>{t.finishDate || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              currentPage={currentPage}
              totalItems={trainings.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        {tab === "review" && (
          <div className="alert alert-info text-center">
            🔍 Review tab will display completed trainings later.
          </div>
        )}

        <TrainingTaskModal
          show={showModal}
          onHide={() => setShowModal(false)}
          moduleKey={activeModule}
          title={activeTitle}
          userId={userId}
          tpStatus={selectedStatus}
          tpScore={selectedScore}
          onSubmitted={() => {
            fetchTrainings();
            setShowModal(false);
          }}
        />
      </div>
    </>
  );
}

export default TrainingPanel;
